import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Settings as SettingsIcon, 
  Users, 
  Globe, 
  DollarSign, 
  CreditCard,
  Bell,
  Shield,
  Smartphone, 
  Mail,
  Key,
  Languages,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Search,
  Filter,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { ADMIN_ROLES } from "@/lib/constants";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

import { paymentMethodsService, PaymentMethod, CreatePaymentMethodRequest, UpdatePaymentMethodRequest } from "@/services/paymentMethodsService";
import { settingsService, SystemSettingsResponse, UpdateSettingRequest, BulkUpdateSettingsRequest, CreateSettingRequest, SystemSetting } from "@/services/settingsService";
import { rolesService, Role, CreateRoleRequest, UpdateRoleRequest, Permission } from "@/services/rolesService";
import { permissionsService, PermissionFilters, CreatePermissionRequest, UpdatePermissionRequest } from "@/services/permissionsService";

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  // Active tab state
  const [activeTab, setActiveTab] = useState('general');
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  // Create setting state
  const [isCreateSettingOpen, setIsCreateSettingOpen] = useState(false);
  const [createSettingData, setCreateSettingData] = useState<CreateSettingRequest>({
    key: '',
    value: '',
    group: 'general',
    description: '',
    is_encrypted: false
  });

  // Settings management state
  const [settingsGroupFilter, setSettingsGroupFilter] = useState('all');
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [isEditSettingOpen, setIsEditSettingOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Roles state
  const [rolesFilters, setRolesFilters] = useState({
    search: '',
    status: '' as 'active' | 'inactive' | ''
  });
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    permissions: [] as number[]
  });

  // Permissions state
  const [permissionsFilters, setPermissionsFilters] = useState<PermissionFilters>({
    search: '',
    resource: '',
    action: '',
    status: '' as 'active' | 'inactive' | ''
  });
  const [permissionsPage, setPermissionsPage] = useState(1);
  const [permissionsLimit, setPermissionsLimit] = useState(10);
  const [isCreatePermissionOpen, setIsCreatePermissionOpen] = useState(false);
  const [isEditPermissionOpen, setIsEditPermissionOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [permissionFormData, setPermissionFormData] = useState({
    name: '',
    description: '',
    resource: '',
    action: '',
    isActive: true
  });

  // Payment methods state
  const [paymentMethodFilters, setPaymentMethodFilters] = useState({
    search: '',
    status: '' as 'active' | 'blocked' | ''
  });
  const [isCreatePaymentMethodOpen, setIsCreatePaymentMethodOpen] = useState(false);
  const [isEditPaymentMethodOpen, setIsEditPaymentMethodOpen] = useState(false);
  const [isViewPaymentMethodOpen, setIsViewPaymentMethodOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState<PaymentMethod | null>(null);
  const [formData, setFormData] = useState<CreatePaymentMethodRequest>({
    provider_name: '',
    provider_code: '',
    image_url: '',
    description: '',
    status: 'active',
    display_order: 1,
    public_key: '',
    secret_key: '',
    merchant_id: '',
    callback_url: ''
  });

  // Initialize settings with default values
  const initializeSettings = (systemData?: SystemSettingsResponse) => {
    if (!systemData) {
      return {
        // Platform settings
        platformName: "RideAdmin",
        timezone: "UTC+3",
        commissionRate: 20,
        supportEmail: "support@rideadmin.com",
        maxTripDistance: 100,
        supportPhone: "+966-800-RIDEHUB",
        emergencyContact: "+966-911",
        
        // SMS Provider settings
        smsProvider: {
          enabled: true,
          providerName: t('twilioProvider') || "Twilio",
          apiKey: "",
          senderId: "",
          status: "active" as "active" | "blocked"
        },
        
        // Firebase settings
        firebase: {
          enabled: true,
          serverKey: "",
          status: "active" as "active" | "blocked"
        },
        
        // Notification preferences
        notifications: {
    email: {
      enabled: true,
      newBooking: true,
      paymentReceived: true,
      driverAssigned: true,
      tripCompleted: true,
      systemAlerts: true
    },
    sms: {
      enabled: true,
      newBooking: false,
      paymentReceived: true,
      driverAssigned: true,
      tripCompleted: false,
      systemAlerts: true
          },
          push: {
            enabled: true,
            newBooking: true,
            paymentReceived: true,
            driverAssigned: true,
            tripCompleted: true,
      systemAlerts: true
    }
        }
      };
    }

    // Extract values from API data
    const generalData = settingsService.settingsArrayToObject(systemData.general);
    const smsData = settingsService.settingsArrayToObject(systemData.sms);
    const firebaseData = settingsService.settingsArrayToObject(systemData.firebase);
    const emailData = settingsService.settingsArrayToObject(systemData.email);

    return {
      // Platform settings from API
      platformName: generalData.app_name || "RideAdmin",
      timezone: "UTC+3",
      commissionRate: 20,
      supportEmail: generalData.support_email || "support@rideadmin.com",
      maxTripDistance: 100,
      supportPhone: generalData.support_phone || "+966-800-RIDEHUB",
      emergencyContact: "+966-911",
      
      // SMS Provider settings from API
      smsProvider: {
        enabled: settingsService.isSettingEnabled(systemData.sms, 'sms_enabled'),
        providerName: smsData.sms_provider_name || t('unifonicProvider') || "Unifonic",
        apiKey: smsData.sms_api_key || "",
        senderId: smsData.sms_sender_id || "",
        status: settingsService.isSettingEnabled(systemData.sms, 'sms_enabled') ? "active" as const : "blocked" as const
      },
      
      // Firebase settings from API
      firebase: {
        enabled: settingsService.isSettingEnabled(systemData.firebase, 'push_notifications_enabled'),
        serverKey: firebaseData.firebase_server_key || "",
        status: settingsService.isSettingEnabled(systemData.firebase, 'push_notifications_enabled') ? "active" as const : "blocked" as const
      },
      
      // Notification preferences
      notifications: {
        email: {
          enabled: settingsService.isSettingEnabled(systemData.email, 'email_enabled'),
          newBooking: true,
          paymentReceived: true,
          driverAssigned: true,
          tripCompleted: true,
          systemAlerts: true
        },
        sms: {
          enabled: settingsService.isSettingEnabled(systemData.sms, 'sms_enabled'),
          newBooking: false,
          paymentReceived: true,
          driverAssigned: true,
          tripCompleted: false,
          systemAlerts: true
        },
        push: {
          enabled: settingsService.isSettingEnabled(systemData.firebase, 'push_notifications_enabled'),
          newBooking: true,
          paymentReceived: true,
          driverAssigned: true,
          tripCompleted: true,
          systemAlerts: true
        }
      }
    };
  };

  // System settings query
  const { data: systemSettingsData, isLoading: isSystemSettingsLoading, error: systemSettingsError } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => settingsService.getSystemSettings(),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 500 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // General & Notification settings
  const [generalSettings, setGeneralSettings] = useState(() => initializeSettings());

  // Update settings when API data is loaded
  React.useEffect(() => {
    if (systemSettingsData?.data) {
      console.log('🔧 Updating settings with API data:', systemSettingsData.data);
      setGeneralSettings(initializeSettings(systemSettingsData.data));
    }
  }, [systemSettingsData]);

  // Payment methods query
  const { data: paymentMethodsData, isLoading: isPaymentMethodsLoading, error: paymentMethodsError } = useQuery({
    queryKey: ['payment-methods', paymentMethodFilters],
    queryFn: () => paymentMethodsService.getPaymentMethods({
      ...paymentMethodFilters,
      status: paymentMethodFilters.status || undefined
    }),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 500 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });


  const updateGroupSettingsMutation = useMutation({
    mutationFn: ({ group, settings }: { group: string; settings: { [key: string]: string } }) => 
      settingsService.updateGroupSettings(group, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast({ title: t("settingsUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Update group settings error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating settings';
      toast({ 
        title: t("failedToUpdateSettings"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const bulkUpdateSettingsMutation = useMutation({
    mutationFn: (data: BulkUpdateSettingsRequest) => settingsService.bulkUpdateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast({ title: t("settingsUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Bulk update settings error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating settings';
      toast({ 
        title: t("failedToUpdateSettings"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const createSettingMutation = useMutation({
    mutationFn: (data: CreateSettingRequest) => settingsService.createSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setIsCreateSettingOpen(false);
      resetCreateSettingForm();
      toast({ title: t("settingCreatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Create setting error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating setting';
      toast({ 
        title: t("failedToCreateSetting"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: (data: UpdateSettingRequest) => 
      settingsService.updateSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setIsEditSettingOpen(false);
      setEditingSetting(null);
      resetCreateSettingForm();
      toast({ title: t("settingUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Update setting error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating setting';
      toast({ 
        title: t("failedToUpdateSetting"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Delete setting mutation
  const deleteSettingMutation = useMutation({
    mutationFn: ({ key, group }: { key: string, group: string }) => 
      settingsService.deleteSetting(key, group),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast({ title: t("settingDeletedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Delete setting error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting setting';
      toast({ 
        title: t("failedToDeleteSetting"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Roles queries - Get all roles for client-side filtering
  const { data: allRolesData, isLoading: isRolesLoading, error: rolesError } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesService.getRoles(),
    retry: (failureCount, error: any) => {
      if (error.response?.status === 500 || error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Client-side filtering and pagination for roles
  const filteredRoles = React.useMemo(() => {
    if (!allRolesData?.data) return [];
    
    let filtered = allRolesData.data;
    
    // Apply search filter
    if (rolesFilters.search) {
      const searchTerm = rolesFilters.search.toLowerCase();
      filtered = filtered.filter(role => 
        role.name.toLowerCase().includes(searchTerm) ||
        role.description.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply status filter
    if (rolesFilters.status) {
      const isActive = rolesFilters.status === 'active';
      filtered = filtered.filter(role => 
        role.isActive === isActive
      );
    }
    
    return filtered;
  }, [allRolesData?.data, rolesFilters]);

  // Create a mock roles data object for compatibility
  const rolesData = React.useMemo(() => {
    if (!allRolesData) return allRolesData;
    
    return {
      ...allRolesData,
      data: filteredRoles // Use filtered data for stats calculations
    };
  }, [allRolesData, filteredRoles]);

  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => rolesService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsCreateRoleOpen(false);
      resetRoleForm();
      toast({ title: t("roleCreatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Create role error:', error);
      console.error('Error response status:', error.response?.status);
      console.error('Error response data:', error.response?.data);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      // Special case: Check if the role was actually created despite the error
      if (error.response?.status === 422 && error.response?.data) {
        const responseData = error.response.data;
        // If the error response contains a valid role object with an ID, treat it as success
        if (responseData.id && responseData.name && responseData.description) {
          console.log('🔧 Role was created despite 422 error, treating as success:', responseData);
          queryClient.invalidateQueries({ queryKey: ['roles'] });
          setIsCreateRoleOpen(false);
          resetRoleForm();
          toast({ title: t("roleCreatedSuccessfully") });
          return;
        }
      }
      
      let errorMessage = 'Error creating role';
      
      if (error.response?.status === 422) {
        console.error('🔧 422 Validation Error Details:');
        console.error('🔧 - Status:', error.response.data?.status);
        console.error('🔧 - Message:', error.response.data?.message);
        console.error('🔧 - Errors:', error.response.data?.errors);
        
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            console.error('🔧 Processing error:', err);
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            if (err.field && err.message) return `${err.field}: ${err.message}`;
            if (err.message) return err.message;
            return JSON.stringify(err);
          });
          errorMessage = validationErrors.join(', ');
          console.error('🔧 Final validation errors:', validationErrors);
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: t("failedToCreateRole"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number, data: UpdateRoleRequest }) => 
      rolesService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsEditRoleOpen(false);
      setEditingRole(null);
      resetRoleForm();
      toast({ title: t("roleUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Update role error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating role';
      toast({ 
        title: t("failedToUpdateRole"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: number) => rolesService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast({ title: t("roleDeletedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Delete role error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting role';
      toast({ 
        title: t("failedToDeleteRole"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const toggleRoleStatusMutation = useMutation({
    mutationFn: (id: number) => rolesService.toggleRoleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast({ title: t("roleStatusToggled") });
    },
    onError: (error: any) => {
      console.error('Toggle role status error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error toggling role status';
      toast({ 
        title: t("failedToToggleRoleStatus"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Permissions queries - Get all permissions for client-side filtering
  const { data: allPermissionsData, isLoading: isPermissionsLoading, error: permissionsError } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsService.getPermissions(),
    retry: (failureCount, error: any) => {
      if (error.response?.status === 500 || error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Client-side filtering and pagination
  const filteredPermissions = React.useMemo(() => {
    if (!allPermissionsData?.data) return [];
    
    let filtered = allPermissionsData.data;
    
    // Apply search filter
    if (permissionsFilters.search) {
      const searchTerm = permissionsFilters.search.toLowerCase();
      filtered = filtered.filter(permission => 
        permission.name.toLowerCase().includes(searchTerm) ||
        permission.description.toLowerCase().includes(searchTerm) ||
        permission.resource.toLowerCase().includes(searchTerm) ||
        permission.action.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply resource filter
    if (permissionsFilters.resource) {
      filtered = filtered.filter(permission => 
        permission.resource === permissionsFilters.resource
      );
    }
    
    // Apply action filter
    if (permissionsFilters.action) {
      filtered = filtered.filter(permission => 
        permission.action === permissionsFilters.action
      );
    }
    
    // Apply status filter
    if (permissionsFilters.status) {
      const isActive = permissionsFilters.status === 'active';
      filtered = filtered.filter(permission => 
        permission.isActive === isActive
      );
    }
    
    return filtered;
  }, [allPermissionsData?.data, permissionsFilters]);

  // Paginated permissions for display
  const paginatedPermissions = React.useMemo(() => {
    const startIndex = (permissionsPage - 1) * permissionsLimit;
    const endIndex = startIndex + permissionsLimit;
    return filteredPermissions.slice(startIndex, endIndex);
  }, [filteredPermissions, permissionsPage, permissionsLimit]);

  // Create a mock permissions data object for compatibility
  const permissionsData = React.useMemo(() => {
    if (!allPermissionsData) return allPermissionsData;
    
    return {
      ...allPermissionsData,
      data: filteredPermissions // Use filtered data for stats calculations
    };
  }, [allPermissionsData, filteredPermissions]);

  const createPermissionMutation = useMutation({
    mutationFn: (data: CreatePermissionRequest) => permissionsService.createPermission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      setIsCreatePermissionOpen(false);
      resetPermissionForm();
      toast({ title: t("permissionCreatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Create permission error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating permission';
      toast({ 
        title: t("failedToCreatePermission"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number, data: UpdatePermissionRequest }) => 
      permissionsService.updatePermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      setIsEditPermissionOpen(false);
      setEditingPermission(null);
      resetPermissionForm();
      toast({ title: t("permissionUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Update permission error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating permission';
      toast({ 
        title: t("failedToUpdatePermission"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const deletePermissionMutation = useMutation({
    mutationFn: (id: number) => permissionsService.deletePermission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      toast({ title: t("permissionDeletedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Delete permission error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting permission';
      toast({ 
        title: t("failedToDeletePermission"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const togglePermissionStatusMutation = useMutation({
    mutationFn: (id: number) => permissionsService.togglePermissionStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      toast({ title: t("permissionStatusToggled") });
    },
    onError: (error: any) => {
      console.error('Toggle permission status error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error toggling permission status';
      toast({ 
        title: t("failedToTogglePermissionStatus"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Payment method mutations
  const createPaymentMethodMutation = useMutation({
    mutationFn: (data: CreatePaymentMethodRequest) => paymentMethodsService.createPaymentMethod(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      setIsCreatePaymentMethodOpen(false);
      resetForm();
      toast({ title: t("paymentMethodCreatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Create payment method error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating payment method';
      toast({ 
        title: t("failedToCreatePaymentMethod"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const updatePaymentMethodMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentMethodRequest }) => 
      paymentMethodsService.updatePaymentMethod(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      setIsEditPaymentMethodOpen(false);
      setEditingPaymentMethod(null);
      resetForm();
      toast({ title: t("paymentMethodUpdatedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Update payment method error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating payment method';
      toast({ 
        title: t("failedToUpdatePaymentMethod"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const deletePaymentMethodMutation = useMutation({
    mutationFn: (id: string) => paymentMethodsService.deletePaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast({ title: t("paymentMethodDeletedSuccessfully") });
    },
    onError: (error: any) => {
      console.error('Delete payment method error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting payment method';
      toast({ 
        title: t("failedToDeletePaymentMethod"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const togglePaymentMethodStatusMutation = useMutation({
    mutationFn: (id: string) => paymentMethodsService.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast({ title: t("paymentMethodStatusToggled") });
    },
    onError: (error: any) => {
      console.error('Toggle payment method status error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error toggling payment method status';
      toast({ 
        title: t("failedToTogglePaymentMethodStatus"), 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  // Handlers
  const handleGeneralSettingChange = (key: string, value: any) => {
    setGeneralSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotificationChange = (category: 'email' | 'sms' | 'push', key: string, value: boolean) => {
    setGeneralSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [category]: {
          ...prev.notifications[category],
          [key]: value
        }
      }
    }));
  };

  const handleSaveSettings = () => {
    // Prepare settings array for bulk update
    const settingsToUpdate: BulkUpdateSettingsRequest = {
      settings: []
    };

    // Add general settings
    if (systemSettingsData?.data?.general) {
      settingsToUpdate.settings.push(
        {
          key: "app_name",
          value: generalSettings.platformName
        },
        {
          key: "support_email", 
          value: generalSettings.supportEmail
        },
        {
          key: "support_phone",
          value: generalSettings.supportPhone
        }
      );
    }

    // Add SMS settings
    if (systemSettingsData?.data?.sms) {
      settingsToUpdate.settings.push(
        {
          key: "sms_enabled",
          value: generalSettings.smsProvider.enabled.toString(),
          is_active: generalSettings.smsProvider.enabled
        },
        {
          key: "sms_provider_name",
          value: generalSettings.smsProvider.providerName
        },
        {
          key: "sms_api_key",
          value: generalSettings.smsProvider.apiKey
        },
        {
          key: "sms_sender_id",
          value: generalSettings.smsProvider.senderId
        }
      );
    }

    // Add Firebase settings
    if (systemSettingsData?.data?.firebase) {
      settingsToUpdate.settings.push(
        {
          key: "push_notifications_enabled",
          value: generalSettings.firebase.enabled.toString(),
          is_active: generalSettings.firebase.enabled
        },
        {
          key: "firebase_server_key",
          value: generalSettings.firebase.serverKey
        }
      );
    }

    // Add Email settings
    if (systemSettingsData?.data?.email) {
      settingsToUpdate.settings.push(
        {
          key: "email_enabled",
          value: generalSettings.notifications.email.enabled.toString(),
          is_active: generalSettings.notifications.email.enabled
        }
      );
    }

    console.log('🔧 Bulk updating settings:', settingsToUpdate);

    if (settingsToUpdate.settings.length > 0) {
      bulkUpdateSettingsMutation.mutate(settingsToUpdate);
    } else {
    toast({ title: t("settingsSaved") });
    }
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
    toast({ title: t("languageChanged") });
  };

  const resetCreateSettingForm = () => {
    setCreateSettingData({
      key: '',
      value: '',
      group: 'general',
      description: '',
      is_encrypted: false
    });
  };

  const handleCreateSetting = () => {
    resetCreateSettingForm();
    setIsCreateSettingOpen(true);
  };

  // Roles helper functions
  const resetRoleForm = () => {
    setRoleFormData({
      name: '',
      description: '',
      isActive: true,
      permissions: []
    });
  };

  const handleCreateRole = () => {
    resetRoleForm();
    setIsCreateRoleOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleFormData({
      name: role.name,
      description: role.description,
      isActive: role.isActive,
      permissions: role.Permissions.map(p => p.id)
    });
    setIsEditRoleOpen(true);
  };

  const handleDeleteRole = (role: Role) => {
    if (window.confirm(t("confirmDeleteRole", { name: role.name }))) {
      deleteRoleMutation.mutate(role.id);
    }
  };

  const handleToggleRoleStatus = (role: Role) => {
    toggleRoleStatusMutation.mutate(role.id);
  };

  // Permissions handlers
  const resetPermissionForm = () => {
    setPermissionFormData({
      name: '',
      description: '',
      resource: '',
      action: '',
      isActive: true
    });
  };

  const handleCreatePermission = () => {
    setEditingPermission(null);
    resetPermissionForm();
    setIsCreatePermissionOpen(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setEditingPermission(permission);
    setPermissionFormData({
      name: permission.name,
      description: permission.description,
      resource: permission.resource,
      action: permission.action,
      isActive: permission.isActive
    });
    setIsEditPermissionOpen(true);
  };

  const handleDeletePermission = (permission: Permission) => {
    if (window.confirm(t("confirmDeletePermission"))) {
      deletePermissionMutation.mutate(permission.id);
    }
  };

  const handleTogglePermissionStatus = (permission: Permission) => {
    togglePermissionStatusMutation.mutate(permission.id);
  };

  const handleSubmitPermission = () => {
    // Basic validation
    if (!permissionFormData.name.trim()) {
      toast({ 
        title: t("nameRequired"), 
        variant: "destructive" 
      });
      return;
    }

    if (!permissionFormData.description.trim()) {
      toast({ 
        title: t("descriptionRequired"), 
        variant: "destructive" 
      });
      return;
    }

    if (!permissionFormData.resource.trim()) {
      toast({ 
        title: t("resourceRequired"), 
        variant: "destructive" 
      });
      return;
    }

    if (!permissionFormData.action.trim()) {
      toast({ 
        title: t("actionRequired"), 
        variant: "destructive" 
      });
      return;
    }

    const apiData = {
      name: permissionFormData.name.trim(),
      description: permissionFormData.description.trim(),
      resource: permissionFormData.resource.trim(),
      action: permissionFormData.action.trim(),
      isActive: permissionFormData.isActive
    };

    if (editingPermission) {
      // Update existing permission
      updatePermissionMutation.mutate({ 
        id: editingPermission.id, 
        data: apiData 
      });
    } else {
      // Create new permission
      createPermissionMutation.mutate(apiData);
    }
  };

  const handlePermissionFilterChange = (key: keyof PermissionFilters, value: string) => {
    // Convert "all" to empty string for API compatibility
    const apiValue = value === "all" ? "" : value;
    setPermissionsFilters((prev: PermissionFilters) => ({ ...prev, [key]: apiValue }));
    // Reset to first page when filters change
    setPermissionsPage(1);
  };

  const handlePermissionsPageChange = (page: number) => {
    setPermissionsPage(page);
  };

  const handlePermissionsLimitChange = (limit: number) => {
    setPermissionsLimit(limit);
    setPermissionsPage(1); // Reset to first page when limit changes
  };

  // Roles filter handlers
  const handleRoleFilterChange = (key: keyof typeof rolesFilters, value: string) => {
    // Convert "all" to empty string for API compatibility
    const apiValue = value === "all" ? "" : value;
    setRolesFilters(prev => ({ ...prev, [key]: apiValue }));
  };

  const handleSubmitRole = () => {
    console.log('🔧 handleSubmitRole called with data:', roleFormData);
    
    // Basic validation
    if (!roleFormData.name.trim()) {
      toast({ 
        title: t("nameRequired"), 
        variant: "destructive" 
      });
      return;
    }

    if (!roleFormData.description.trim()) {
      toast({ 
        title: t("descriptionRequired"), 
        variant: "destructive" 
      });
      return;
    }

    // Prepare the data for API call
    const apiData = {
      name: roleFormData.name.trim(),
      description: roleFormData.description.trim(),
      isActive: roleFormData.isActive
    };
    
    console.log('🔧 Sending API data:', apiData);

    if (editingRole) {
      // Update existing role
      updateRoleMutation.mutate({ 
        id: editingRole.id, 
        data: apiData 
      });
    } else {
      // Create new role
      createRoleMutation.mutate(apiData);
    }
  };

  const handleSubmitCreateSetting = () => {
    // Basic validation
    if (!createSettingData.key.trim()) {
      toast({ title: t("keyRequired"), variant: "destructive" });
      return;
    }
    if (!createSettingData.value.trim()) {
      toast({ title: t("valueRequired"), variant: "destructive" });
      return;
    }

    // Validate key format (alphanumeric with underscores)
    const keyPattern = /^[a-zA-Z0-9_]+$/;
    if (!keyPattern.test(createSettingData.key)) {
      toast({ 
        title: t("invalidKeyFormat"), 
        description: t("keyFormatDescription"),
        variant: "destructive" 
      });
      return;
    }

    if (editingSetting) {
      // Update existing setting
      const updateData: UpdateSettingRequest = {
        key: editingSetting.key,
        value: createSettingData.value,
        group: editingSetting.group
      };
      
      updateSettingMutation.mutate(updateData);
    } else {
      // Create new setting
      createSettingMutation.mutate(createSettingData);
    }
  };

  // Get all settings as a flat array
  const allSettings = React.useMemo(() => {
    if (!systemSettingsData?.data) return [];
    
    const settings: SystemSetting[] = [];
    Object.entries(systemSettingsData.data).forEach(([group, groupSettings]) => {
      if (Array.isArray(groupSettings)) {
        settings.push(...groupSettings);
      }
    });
    return settings;
  }, [systemSettingsData]);

  // Filter settings by group
  const filteredSettings = React.useMemo(() => {
    if (settingsGroupFilter === 'all') return allSettings;
    return allSettings.filter(setting => setting.group === settingsGroupFilter);
  }, [allSettings, settingsGroupFilter]);

  // Pagination calculations
  const totalItems = filteredSettings.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSettings = filteredSettings.slice(startIndex, endIndex);

  // Reset to first page when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [settingsGroupFilter]);

  const handleEditSetting = (setting: SystemSetting) => {
    setEditingSetting(setting);
    setCreateSettingData({
      key: setting.key,
      value: setting.value || '',
      group: setting.group as 'sms' | 'firebase' | 'email' | 'general',
      description: setting.description || '',
      is_encrypted: setting.is_encrypted
    });
    setIsEditSettingOpen(true);
  };

  const handleDeleteSetting = (setting: SystemSetting) => {
    // Show confirmation dialog
    if (window.confirm(t("confirmDeleteSetting", { key: setting.key }))) {
      deleteSettingMutation.mutate({ 
        key: setting.key, 
        group: setting.group 
      });
    }
  };

  const resetForm = () => {
    setFormData({
      provider_name: '',
      provider_code: '',
      image_url: '',
      description: '',
      status: 'active',
      display_order: 1,
      public_key: '',
      secret_key: '',
      merchant_id: '',
      callback_url: ''
    });
  };

  const handleCreatePaymentMethod = () => {
    resetForm();
    setIsCreatePaymentMethodOpen(true);
  };

  const handleEditPaymentMethod = (paymentMethod: PaymentMethod) => {
    setEditingPaymentMethod(paymentMethod);
    setFormData({
      provider_name: paymentMethod.provider_name,
      provider_code: paymentMethod.provider_code,
      image_url: paymentMethod.image_url || '',
      description: paymentMethod.description,
      status: paymentMethod.status,
      display_order: paymentMethod.display_order,
      public_key: paymentMethod.public_key || '',
      secret_key: paymentMethod.secret_key || '',
      merchant_id: paymentMethod.merchant_id || '',
      callback_url: paymentMethod.callback_url || ''
    });
    setIsEditPaymentMethodOpen(true);
  };

  const handleSubmit = () => {
    if (editingPaymentMethod) {
      updatePaymentMethodMutation.mutate({ id: editingPaymentMethod.id, data: formData });
    } else {
      createPaymentMethodMutation.mutate(formData);
    }
  };

  const handleDeletePaymentMethod = (paymentMethod: PaymentMethod) => {
    deletePaymentMethodMutation.mutate(paymentMethod.id);
  };

  const handleToggleStatus = (paymentMethod: PaymentMethod) => {
    togglePaymentMethodStatusMutation.mutate(paymentMethod.id);
  };

  const paymentMethods = paymentMethodsData?.data?.payment_methods || [];
  const stats = paymentMethodsData?.data ? paymentMethodsService.calculateStats(paymentMethods) : {
    total_methods: 0,
    active_methods: 0,
    inactive_methods: 0
  };

  const isBackendUnavailable = paymentMethodsError?.response?.status === 500 || paymentMethodsError?.response?.status === 404;

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{t("settingsTitle")}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {t("settingsSubtitle")}
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'general'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <Globe className="h-4 w-4" />
          {t("generalAndNotifications")}
        </button>
        
        <button
          onClick={() => setActiveTab('payment')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'payment'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <CreditCard className="h-4 w-4" />
          {t("paymentMethods")}
        </button>
        
        <button
          onClick={() => setActiveTab('language')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'language'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <Languages className="h-4 w-4" />
          {t("language")}
        </button>
        
        <button
          onClick={() => setActiveTab('manage')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'manage'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <SettingsIcon className="h-4 w-4" />
          {t("manageSettings")}
        </button>
        
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'roles'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4" />
          {t("roles")}
        </button>
        
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'permissions'
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
          }`}
        >
          <Shield className="h-4 w-4" />
          {t("permissions")}
        </button>
      </div>

      {/* Content Area */}
      <div className="space-y-6">
        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
          {/* Platform Settings */}
          <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                {t("platformSettings")}
              </CardTitle>
                <CardDescription>{t("configurePlatformSettings")}</CardDescription>
            </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                          <Label htmlFor="platformName">{t("platformName")}</Label>
                    <Input
                            id="platformName"
                            value={generalSettings.platformName}
                            onChange={(e) => handleGeneralSettingChange('platformName', e.target.value)}
                      placeholder={t("enterPlatformName")}
                    />
                  </div>
                  <div className="space-y-2">
                          <Label htmlFor="supportEmail">{t("supportEmail")}</Label>
                          <Input
                            id="supportEmail"
                            type="email"
                            value={generalSettings.supportEmail}
                            onChange={(e) => handleGeneralSettingChange('supportEmail', e.target.value)}
                            placeholder={t("enterSupportEmail")}
                          />
                  </div>
                  <div className="space-y-2">
                          <Label htmlFor="supportPhone">{t("supportPhone")}</Label>
                    <Input
                            id="supportPhone"
                            value={generalSettings.supportPhone}
                            onChange={(e) => handleGeneralSettingChange('supportPhone', e.target.value)}
                            placeholder={t("enterSupportPhone")}
                    />
                  </div>
                </div>
                    </CardContent>
                  </Card>

            {/* SMS Provider Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  {t("smsProviderSettings")}
                </CardTitle>
                <CardDescription>{t("smsProviderConfiguration")}</CardDescription>
              </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label>{t("smsProvider")}</Label>
                          <p className="text-sm text-muted-foreground">{t("enableSmsNotifications")}</p>
                        </div>
                        <Switch
                          checked={generalSettings.smsProvider.enabled}
                          onCheckedChange={(checked) => 
                            setGeneralSettings(prev => ({
                              ...prev,
                              smsProvider: { ...prev.smsProvider, enabled: checked }
                            }))
                          }
                    />
                  </div>
                  
                      {generalSettings.smsProvider.enabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                            <Label htmlFor="smsProviderName">{t("providerName")}</Label>
                    <Input
                              id="smsProviderName"
                              value={generalSettings.smsProvider.providerName}
                              onChange={(e) => 
                                setGeneralSettings(prev => ({
                                  ...prev,
                                  smsProvider: { ...prev.smsProvider, providerName: e.target.value }
                                }))
                              }
                              placeholder={t("enterProviderName")}
                    />
                  </div>
                  <div className="space-y-2">
                            <Label htmlFor="smsApiKey">{t("smsApiKey")}</Label>
                    <Input
                              id="smsApiKey"
                              type="password"
                              value={generalSettings.smsProvider.apiKey}
                              onChange={(e) => 
                                setGeneralSettings(prev => ({
                                  ...prev,
                                  smsProvider: { ...prev.smsProvider, apiKey: e.target.value }
                                }))
                              }
                              placeholder={t("enterSmsApiKey")}
                    />
                  </div>
                  <div className="space-y-2">
                            <Label htmlFor="senderId">{t("senderId")}</Label>
                    <Input
                              id="senderId"
                              value={generalSettings.smsProvider.senderId}
                              onChange={(e) => 
                                setGeneralSettings(prev => ({
                                  ...prev,
                                  smsProvider: { ...prev.smsProvider, senderId: e.target.value }
                                }))
                              }
                              placeholder={t("enterSenderId")}
                    />
                  </div>
                </div>
                      )}
            </CardContent>
          </Card>

            {/* Firebase Settings */}
          <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t("firebaseSettings")}
              </CardTitle>
                <CardDescription>{t("firebaseConfiguration")}</CardDescription>
            </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label>{t("pushNotifications")}</Label>
                          <p className="text-sm text-muted-foreground">{t("pushNotificationsDesc")}</p>
                        </div>
                        <Switch
                          checked={generalSettings.firebase.enabled}
                          onCheckedChange={(checked) => 
                            setGeneralSettings(prev => ({
                              ...prev,
                              firebase: { ...prev.firebase, enabled: checked }
                            }))
                          }
                    />
                  </div>
                  
                      {generalSettings.firebase.enabled && (
                  <div className="space-y-2">
                          <Label htmlFor="firebaseServerKey">{t("firebaseServerKey")}</Label>
                      <Input
                            id="firebaseServerKey"
                            type="password"
                            value={generalSettings.firebase.serverKey}
                            onChange={(e) => 
                              setGeneralSettings(prev => ({
                                ...prev,
                                firebase: { ...prev.firebase, serverKey: e.target.value }
                              }))
                            }
                            placeholder={t("enterFirebaseServerKey")}
                      />
                    </div>
                      )}
                    </CardContent>
                  </Card>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button 
                variant="outline"
                onClick={handleCreateSetting}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("createSetting")}
              </Button>
              <div className="flex gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={async () => {
                  console.log('🔧 Testing bulk settings API...');
                  const testData: BulkUpdateSettingsRequest = {
                    settings: [
                      {
                        key: "sms_api_key",
                        value: "test-api-key-123"
                      },
                      {
                        key: "sms_sender_id", 
                        value: "TestApp"
                      },
                      {
                        key: "sms_enabled",
                        value: "true",
                        is_active: true
                      }
                    ]
                  };
                  
                  try {
                    const result = await settingsService.bulkUpdateSettings(testData);
                    console.log('🔧 Test result:', result);
                    toast({ 
                      title: result.success ? "API Test Successful" : "API Test Failed", 
                      description: result.message,
                      variant: result.success ? "default" : "destructive"
                    });
                  } catch (error) {
                    console.error('🔧 Test error:', error);
                    toast({ 
                      title: "API Test Error", 
                      description: t('checkConsoleForDetails') || "تحقق من وحدة التحكم للحصول على التفاصيل",
                      variant: "destructive"
                    });
                  }
                }}
              >
                Test API
              </Button>
              <Button 
                onClick={handleSaveSettings}
                disabled={bulkUpdateSettingsMutation.isPending || updateGroupSettingsMutation.isPending}
              >
                {(bulkUpdateSettingsMutation.isPending || updateGroupSettingsMutation.isPending) ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    {t("saving")}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t("saveAllSettings")}
                  </>
                )}
              </Button>
                  </div>
                </div>
                  </div>
        )}

        {/* Payment Methods Tab */}
        {activeTab === 'payment' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{t("paymentMethods")}</h2>
                <p className="text-muted-foreground">
                  {t("paymentMethodsList")}
                </p>
                    </div>
              <Button 
                onClick={handleCreatePaymentMethod}
                disabled={isBackendUnavailable}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("addPaymentMethod")}
              </Button>
                  </div>
                  
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t("totalMethods")}</p>
                      <p className="text-2xl font-bold">{stats.total_methods}</p>
                    </div>
                    <CreditCard className="h-8 w-8 text-muted-foreground" />
                </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t("activeMethods")}</p>
                      <p className="text-2xl font-bold text-green-600">{stats.active_methods}</p>
              </div>
                    <ToggleRight className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t("inactiveMethods")}</p>
                      <p className="text-2xl font-bold text-red-600">{stats.inactive_methods}</p>
                    </div>
                    <ToggleLeft className="h-8 w-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
                  </div>
                  
            {/* Payment Methods Table */}
          <Card>
              <CardContent className="p-6">
                      {isBackendUnavailable ? (
                        <div className="text-center py-12">
                          <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                          <h3 className="text-lg font-semibold mb-2">{t("paymentMethodsEndpointNotAvailable")}</h3>
                          <p className="text-muted-foreground mb-4">{t("paymentMethodsEndpointNotImplemented")}</p>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <p className="font-medium">{t("requiredEndpoints")}:</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li>GET /admin/payment-methods</li>
                              <li>POST /admin/payment-methods</li>
                              <li>PUT /admin/payment-methods/:id</li>
                              <li>DELETE /admin/payment-methods/:id</li>
                              <li>PATCH /admin/payment-methods/:id/toggle-status</li>
                            </ul>
                          </div>
                          <div className="flex gap-2 justify-center mt-4">
                            <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['payment-methods'] })}>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              {t("retryConnection")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                <div className="space-y-4">
                          {/* Filters */}
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                                  placeholder={t("searchPaymentMethods")}
                                  value={paymentMethodFilters.search}
                                  onChange={(e) => setPaymentMethodFilters(prev => ({ ...prev, search: e.target.value }))}
                                  className="pl-10"
                    />
                  </div>
                </div>
                             <Select
                               value={paymentMethodFilters.status || 'all'}
                               onValueChange={(value: 'active' | 'blocked' | 'all') => 
                                 setPaymentMethodFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }))
                               }
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder={t("allStatuses")} />
                              </SelectTrigger>
                               <SelectContent>
                                 <SelectItem value="all">{t("allStatuses")}</SelectItem>
                                 <SelectItem value="active">{t("active")}</SelectItem>
                                 <SelectItem value="blocked">{t("blocked")}</SelectItem>
                      </SelectContent>
                    </Select>
              </div>
              
                          {/* Table */}
                          {isPaymentMethodsLoading ? (
                            <div className="space-y-3">
                              {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                              ))}
                  </div>
                          ) : paymentMethods.length === 0 ? (
                            <div className="text-center py-12">
                              <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                              <h3 className="text-lg font-semibold mb-2">{t("noPaymentMethods")}</h3>
                              <p className="text-muted-foreground mb-4">{t("createFirstPaymentMethod")}</p>
                              <Button onClick={handleCreatePaymentMethod}>
                                <Plus className="mr-2 h-4 w-4" />
                                {t("addPaymentMethod")}
                </Button>
              </div>
                          ) : (
                            <div className="rounded-lg border overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-muted/50">
                                    <TableHead>{t("provider")}</TableHead>
                                    <TableHead>{t("code")}</TableHead>
                                    <TableHead>{t("status")}</TableHead>
                                    <TableHead>{t("displayOrder")}</TableHead>
                                    <TableHead>{t("createdAt")}</TableHead>
                                    <TableHead className="text-right">{t("actions")}</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {paymentMethods.map((method) => (
                                    <TableRow key={method.id} className="hover:bg-muted/50">
                                      <TableCell>
                                        <div className="flex items-center gap-3">
                                          {method.image_url && (
                                            <img 
                                              src={method.image_url} 
                                              alt={method.provider_name}
                                              className="w-8 h-8 rounded object-cover"
                                            />
                                          )}
                                          <div>
                                            <p className="font-medium">{method.provider_name}</p>
                                            <p className="text-sm text-muted-foreground">{method.description}</p>
              </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="outline">{method.provider_code}</Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Badge 
                                          variant={method.status === 'active' ? 'default' : 'secondary'}
                                          className={method.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                                        >
                                          {t(method.status)}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>{method.display_order}</TableCell>
                                      <TableCell>{new Date(method.createdAt).toLocaleDateString()}</TableCell>
                                      <TableCell className="text-right">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                              <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => {
                                              setSelectedPaymentMethod(method);
                                              setIsViewPaymentMethodOpen(true);
                                            }}>
                                              <Eye className="mr-2 h-4 w-4" />
                                              {t("viewDetails")}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleEditPaymentMethod(method)}>
                                              <Edit className="mr-2 h-4 w-4" />
                                              {t("edit")}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleToggleStatus(method)}>
                                              {method.status === 'active' ? (
                                                <>
                                                  <ToggleLeft className="mr-2 h-4 w-4" />
                                                  {t("block")}
                                                </>
                                              ) : (
                                                <>
                                                  <ToggleRight className="mr-2 h-4 w-4" />
                                                  {t("activate")}
                                                </>
                                              )}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                              onClick={() => handleDeletePaymentMethod(method)}
                                              className="text-red-600"
                                            >
                                              <Trash2 className="mr-2 h-4 w-4" />
                                              {t("delete")}
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      )}
            </CardContent>
          </Card>
                </div>
              )}

        {/* Language Settings Tab */}
        {activeTab === 'language' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-5 w-5" />
                {t("languageSettings")}
              </CardTitle>
              <CardDescription>{t("selectPreferredLanguage")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`p-4 rounded-lg border transition-colors ${
                    selectedLanguage === 'en'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                    <div className="flex items-center gap-3">
                    <div className="text-2xl">🇺🇸</div>
                    <div className="text-left">
                      <h3 className="font-medium">English</h3>
                      <p className="text-sm text-muted-foreground">English (United States)</p>
                      </div>
                      </div>
                </button>
                <button
                  onClick={() => handleLanguageChange('ar')}
                  className={`p-4 rounded-lg border transition-colors ${
                    selectedLanguage === 'ar'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">🇸🇦</div>
                    <div className="text-left">
                      <h3 className="font-medium">العربية</h3>
                      <p className="text-sm text-muted-foreground">Arabic (Saudi Arabia)</p>
                    </div>
                  </div>
                </button>
                      </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Management Tab */}
        {activeTab === 'manage' && (
              <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                      <div>
                <h2 className="text-2xl font-bold tracking-tight">{t("manageSettings")}</h2>
                <p className="text-muted-foreground">
                  {t("manageSettingsDescription")}
                </p>
                      </div>
              <Button onClick={handleCreateSetting}>
                <Plus className="mr-2 h-4 w-4" />
                {t("createSetting")}
              </Button>
                    </div>

            {/* Settings Table */}
            <Card>
              <CardContent className="p-6">
                {isSystemSettingsLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : systemSettingsError ? (
                  <div className="text-center py-12">
                    <SettingsIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("settingsLoadError")}</h3>
                    <p className="text-muted-foreground mb-4">{t("settingsLoadErrorDescription")}</p>
                    <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['system-settings'] })}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t("retry")}
                    </Button>
                      </div>
                ) : (
                <div className="space-y-4">
                    {/* Filter by Group */}
                    <div className="flex gap-4">
                      <Select
                        value={settingsGroupFilter}
                        onValueChange={setSettingsGroupFilter}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder={t("allGroups")} />
                      </SelectTrigger>
                      <SelectContent>
                          <SelectItem value="all">{t("allGroups")}</SelectItem>
                          <SelectItem value="general">{t("general")}</SelectItem>
                          <SelectItem value="sms">{t("sms")}</SelectItem>
                          <SelectItem value="firebase">{t("firebase")}</SelectItem>
                          <SelectItem value="email">{t("email")}</SelectItem>
                        </SelectContent>
                      </Select>
                          </div>

                    {/* Settings Table */}
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>{t("key")}</TableHead>
                            <TableHead>{t("value")}</TableHead>
                            <TableHead>{t("group")}</TableHead>
                            <TableHead>{t("description")}</TableHead>
                            <TableHead>{t("encrypted")}</TableHead>
                            <TableHead>{t("lastUpdated")}</TableHead>
                            <TableHead className="text-right">{t("actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedSettings.map((setting) => (
                            <TableRow key={setting.id} className="hover:bg-muted/50">
                              <TableCell>
                                <code className="text-sm bg-muted px-2 py-1 rounded">{setting.key}</code>
                              </TableCell>
                              <TableCell>
                                {setting.is_encrypted ? (
                          <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">••••••••</span>
                                    <Badge variant="secondary" className="text-xs">
                                      {t("encrypted")}
                                    </Badge>
                          </div>
                                ) : (
                                  <span className="max-w-xs truncate block" title={setting.value || ''}>
                                    {setting.value || t("noValue")}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{setting.group}</Badge>
                              </TableCell>
                              <TableCell>
                                <span className="max-w-xs truncate block" title={setting.description || ''}>
                                  {setting.description || t("noDescription")}
                                </span>
                              </TableCell>
                              <TableCell>
                                {setting.is_encrypted ? (
                                  <Badge variant="default" className="bg-green-100 text-green-800">
                                    {t("yes")}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">{t("no")}</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {new Date(setting.updatedAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEditSetting(setting)}>
                                      <Edit className="mr-2 h-4 w-4" />
                                      {t("edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteSetting(setting)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      {t("delete")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                  </div>
                  
                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                      <div className="flex items-center justify-between px-2 py-4">
                        <div className="flex items-center space-x-2">
                    <p className="text-sm text-muted-foreground">
                            {t("showingResults", { 
                              start: startIndex + 1, 
                              end: Math.min(endIndex, totalItems), 
                              total: totalItems 
                            })}
                    </p>
                  </div>
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium">{t("itemsPerPage")}</p>
                            <Select
                              value={itemsPerPage.toString()}
                              onValueChange={(value) => {
                                setItemsPerPage(Number(value));
                                setCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="h-8 w-[70px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent side="top">
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                              </SelectContent>
                            </Select>
                </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage <= 1}
                            >
                              {t("previous")}
                            </Button>
                            <div className="flex items-center space-x-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(page => {
                                  // Show first page, last page, current page, and pages around current
                                  return page === 1 || 
                                         page === totalPages || 
                                         Math.abs(page - currentPage) <= 1;
                                })
                                .map((page, index, array) => (
                                  <React.Fragment key={page}>
                                    {index > 0 && array[index - 1] !== page - 1 && (
                                      <span className="px-2 text-muted-foreground">...</span>
                                    )}
                                    <Button
                                      variant={currentPage === page ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setCurrentPage(page)}
                                      className="h-8 w-8"
                                    >
                                      {page}
                                    </Button>
                                  </React.Fragment>
                                ))}
              </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage >= totalPages}
                            >
                              {t("next")}
                            </Button>
                  </div>
                      </div>
                      </div>
                    )}

                    {filteredSettings.length === 0 && (
                      <div className="text-center py-12">
                        <SettingsIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold mb-2">{t("noSettings")}</h3>
                        <p className="text-muted-foreground mb-4">{t("noSettingsDescription")}</p>
                        <Button onClick={handleCreateSetting}>
                          <Plus className="mr-2 h-4 w-4" />
                          {t("createFirstSetting")}
                        </Button>
                    </div>
                  )}
                </div>
                )}
            </CardContent>
          </Card>
                    </div>
        )}

        {/* Roles Management Tab */}
        {activeTab === 'roles' && (
              <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                      <div>
                <h2 className="text-2xl font-bold tracking-tight">{t("roles")}</h2>
                <p className="text-muted-foreground">
                  {t("rolesDescription")}
                </p>
                      </div>
              <Button onClick={handleCreateRole}>
                <Plus className="mr-2 h-4 w-4" />
                {t("createRole")}
              </Button>
                    </div>

            {/* Statistics Cards */}
            {rolesData?.data && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("totalRoles")}</p>
                        <p className="text-2xl font-bold">{rolesService.calculateStats(rolesData.data).total_roles}</p>
                  </div>
                      <Users className="h-8 w-8 text-muted-foreground" />
                      </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("activeRoles")}</p>
                        <p className="text-2xl font-bold text-green-600">{rolesService.calculateStats(rolesData.data).active_roles}</p>
                      </div>
                      <Shield className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("inactiveRoles")}</p>
                        <p className="text-2xl font-bold text-red-600">{rolesService.calculateStats(rolesData.data).inactive_roles}</p>
                      </div>
                      <Shield className="h-8 w-8 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
                  </div>
            )}

            {/* Roles Table */}
            <Card>
              <CardContent className="p-6">
                {isRolesLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                      </div>
                ) : rolesError ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("rolesLoadError")}</h3>
                    <p className="text-muted-foreground mb-4">{t("rolesEndpointNotAvailable")}</p>
                    <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['roles'] })}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t("retry")}
                    </Button>
                  </div>
                ) : rolesData?.data && rolesData.data.length > 0 ? (
                  <div className="space-y-4">
                    {/* Filter Controls */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                      <div className="flex flex-col sm:flex-row gap-4 flex-1">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                          <Input
                            placeholder={t("searchRoles")}
                            value={rolesFilters.search}
                            onChange={(e) => handleRoleFilterChange('search', e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        <Select
                          value={rolesFilters.status || "all"}
                          onValueChange={(value) => handleRoleFilterChange('status', value)}
                        >
                          <SelectTrigger className="w-full sm:w-[180px]">
                            <SelectValue placeholder={t("allStatuses")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t("allStatuses")}</SelectItem>
                            <SelectItem value="active">{t("active")}</SelectItem>
                            <SelectItem value="inactive">{t("inactive")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        {(rolesFilters.search || rolesFilters.status) && (
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setRolesFilters({
                                search: '',
                                status: ''
                              });
                            }}
                            className="w-full sm:w-auto"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {t("clearFilters")}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Results Counter */}
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        {t("showingResults", { 
                          showing: filteredRoles.length, 
                          total: allRolesData?.data?.length || 0 
                        })}
                        {(rolesFilters.search || rolesFilters.status) && (
                          <span className="ml-1">({t("filtered")})</span>
                        )}
                      </p>
                    </div>

                    {/* Roles Table */}
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>{t("name")}</TableHead>
                            <TableHead>{t("description")}</TableHead>
                            <TableHead>{t("permissions")}</TableHead>
                            <TableHead>{t("status")}</TableHead>
                            <TableHead>{t("createdAt")}</TableHead>
                            <TableHead className="text-right">{t("actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredRoles.map((role) => (
                            <TableRow key={role.id} className="hover:bg-muted/50">
                              <TableCell>
                                <div className="font-medium">{role.name}</div>
                              </TableCell>
                              <TableCell>
                                <span className="max-w-xs truncate block" title={role.description}>
                                  {role.description}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {role.Permissions.length} {t("permissions")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={role.isActive ? "default" : "secondary"}>
                                  {role.isActive ? t("active") : t("inactive")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {new Date(role.createdAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEditRole(role)}>
                                      <Edit className="mr-2 h-4 w-4" />
                                      {t("edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleToggleRoleStatus(role)}>
                                      {role.isActive ? (
                                        <>
                                          <ToggleLeft className="mr-2 h-4 w-4" />
                                          {t("deactivate")}
                                        </>
                                      ) : (
                                        <>
                                          <ToggleRight className="mr-2 h-4 w-4" />
                                          {t("activate")}
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteRole(role)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      {t("delete")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredRoles.length === 0 && allRolesData?.data && allRolesData.data.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8">
                                <div className="flex flex-col items-center">
                                  <Search className="h-8 w-8 text-muted-foreground mb-2" />
                                  <p className="text-muted-foreground">{t("noRolesMatch")}</p>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => {
                                      setRolesFilters({
                                        search: '',
                                        status: ''
                                      });
                                    }}
                                    className="mt-2"
                                  >
                                    {t("clearFilters")}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("noRoles")}</h3>
                    <p className="text-muted-foreground mb-4">{t("noRolesDescription")}</p>
                    <Button onClick={handleCreateRole}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t("createFirstRole")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Permissions Management Tab */}
        {activeTab === 'permissions' && (
          <div className="space-y-6">
            {/* Permissions Statistics */}
            {permissionsData?.success && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("totalPermissions")}</p>
                        <p className="text-2xl font-bold">{permissionsData.data ? permissionsService.calculateStats(permissionsData.data).total_permissions : 0}</p>
                      </div>
                      <Shield className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("activePermissions")}</p>
                        <p className="text-2xl font-bold text-green-600">{permissionsData.data ? permissionsService.calculateStats(permissionsData.data).active_permissions : 0}</p>
                      </div>
                      <Shield className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t("inactivePermissions")}</p>
                        <p className="text-2xl font-bold text-red-600">{permissionsData.data ? permissionsService.calculateStats(permissionsData.data).inactive_permissions : 0}</p>
                  </div>
                      <Shield className="h-8 w-8 text-red-600" />
                      </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Permissions Filters and Actions */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                        placeholder={t("searchPermissions")}
                        value={permissionsFilters.search}
                        onChange={(e) => handlePermissionFilterChange('search', e.target.value)}
                        className="pl-10"
                        />
                      </div>
                    <Select
                      value={permissionsFilters.resource || "all"}
                      onValueChange={(value) => handlePermissionFilterChange('resource', value)}
                    >
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder={t("allResources")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("allResources")}</SelectItem>
                        {permissionsData?.success && permissionsData.data && permissionsService.calculateStats(permissionsData.data).resources.map((resource) => (
                          <SelectItem key={resource} value={resource}>
                            {resource}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={permissionsFilters.action || "all"}
                      onValueChange={(value) => handlePermissionFilterChange('action', value)}
                    >
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder={t("allActions")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("allActions")}</SelectItem>
                        {permissionsData?.success && permissionsData.data && permissionsService.calculateStats(permissionsData.data).actions.map((action) => (
                          <SelectItem key={action} value={action}>
                            {action}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={permissionsFilters.status || "all"}
                      onValueChange={(value) => handlePermissionFilterChange('status', value as 'active' | 'inactive' | '')}
                    >
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder={t("allStatuses")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("allStatuses")}</SelectItem>
                        <SelectItem value="active">{t("active")}</SelectItem>
                        <SelectItem value="inactive">{t("inactive")}</SelectItem>
                      </SelectContent>
                    </Select>
                    </div>
                  <div className="flex gap-2">
                    {(permissionsFilters.search || permissionsFilters.resource || permissionsFilters.action || permissionsFilters.status) && (
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setPermissionsFilters({
                            search: '',
                            resource: '',
                            action: '',
                            status: '' as 'active' | 'inactive' | ''
                          });
                          setPermissionsPage(1);
                        }}
                        className="w-full sm:w-auto"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t("clearFilters")}
                      </Button>
                    )}
                    <Button onClick={handleCreatePermission} className="w-full sm:w-auto">
                      <Plus className="h-4 w-4 mr-2" />
                      {t("createPermission")}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Permissions Table */}
            <Card>
              <CardContent className="p-6">
                {isPermissionsLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
                    <p>{t("loading")}</p>
                      </div>
                ) : permissionsError || !permissionsData?.success ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t("permissionsLoadError")}</h3>
                    <p className="text-muted-foreground mb-4">{t("permissionsEndpointNotAvailable")}</p>
                    <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['permissions'] })}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t("retry")}
                    </Button>
                      </div>
                ) : filteredPermissions.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    {allPermissionsData?.data && allPermissionsData.data.length > 0 ? (
                      // Has data but no filtered results
                      <>
                        <h3 className="text-lg font-semibold mb-2">{t("noPermissionsMatch")}</h3>
                        <p className="text-muted-foreground mb-4">{t("noPermissionsDescription")}</p>
                        <div className="flex gap-2 justify-center">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setPermissionsFilters({
                                search: '',
                                resource: '',
                                action: '',
                                status: '' as 'active' | 'inactive' | ''
                              });
                              setPermissionsPage(1);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {t("clearFilters")}
                          </Button>
                          <Button onClick={handleCreatePermission}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t("createPermission")}
                          </Button>
                        </div>
                      </>
                    ) : (
                      // No data at all
                      <>
                        <h3 className="text-lg font-semibold mb-2">{t("noPermissions")}</h3>
                        <p className="text-muted-foreground mb-4">{t("createFirstPermission")}</p>
                        <Button onClick={handleCreatePermission}>
                          <Plus className="h-4 w-4 mr-2" />
                          {t("createPermission")}
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("name")}</TableHead>
                          <TableHead>{t("description")}</TableHead>
                          <TableHead>{t("resource")}</TableHead>
                          <TableHead>{t("action")}</TableHead>
                          <TableHead>{t("status")}</TableHead>
                          <TableHead>{t("actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedPermissions?.map((permission) => (
                          <TableRow key={permission.id}>
                            <TableCell className="font-medium">{permission.name}</TableCell>
                            <TableCell>{permission.description}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{permission.resource}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{permission.action}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={permission.isActive ? "default" : "secondary"}>
                                {permission.isActive ? t("active") : t("inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEditPermission(permission)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    {t("edit")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTogglePermissionStatus(permission)}>
                                    {permission.isActive ? (
                                      <>
                                        <ToggleLeft className="h-4 w-4 mr-2" />
                                        {t("deactivate")}
                                      </>
                                    ) : (
                                      <>
                                        <ToggleRight className="h-4 w-4 mr-2" />
                                        {t("activate")}
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleDeletePermission(permission)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t("delete")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between space-x-2 py-4">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-muted-foreground">
                          {t("showingResults", { 
                            start: filteredPermissions.length > 0 ? ((permissionsPage - 1) * permissionsLimit) + 1 : 0,
                            end: Math.min(permissionsPage * permissionsLimit, filteredPermissions.length),
                            total: filteredPermissions.length
                          })}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-muted-foreground">{t("itemsPerPage")}</p>
                        <Select
                          value={permissionsLimit.toString()}
                          onValueChange={(value) => handlePermissionsLimitChange(parseInt(value))}
                        >
                          <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent side="top">
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Pagination Navigation */}
                    {filteredPermissions.length > permissionsLimit && (
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              onClick={() => handlePermissionsPageChange(Math.max(1, permissionsPage - 1))}
                              className={permissionsPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                          
                          {/* Page Numbers */}
                          {Array.from({ length: Math.ceil(filteredPermissions.length / permissionsLimit) }, (_, i) => i + 1)
                            .filter(page => {
                              const totalPages = Math.ceil(filteredPermissions.length / permissionsLimit);
                              return page === 1 || 
                                     page === totalPages || 
                                     (page >= permissionsPage - 1 && page <= permissionsPage + 1);
                            })
                            .map((page, index, array) => (
                              <React.Fragment key={page}>
                                {index > 0 && array[index - 1] !== page - 1 && (
                                  <PaginationItem>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                )}
                                <PaginationItem>
                                  <PaginationLink
                                    onClick={() => handlePermissionsPageChange(page)}
                                    isActive={page === permissionsPage}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              </React.Fragment>
                            ))}
                          
                          <PaginationItem>
                            <PaginationNext 
                              onClick={() => handlePermissionsPageChange(Math.min(Math.ceil(filteredPermissions.length / permissionsLimit), permissionsPage + 1))}
                              className={permissionsPage >= Math.ceil(filteredPermissions.length / permissionsLimit) ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create/Edit Payment Method Dialog */}
      <Dialog open={isCreatePaymentMethodOpen || isEditPaymentMethodOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreatePaymentMethodOpen(false);
          setIsEditPaymentMethodOpen(false);
          setEditingPaymentMethod(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPaymentMethod ? t("editPaymentMethod") : t("addPaymentMethod")}
            </DialogTitle>
            <DialogDescription>
              {editingPaymentMethod ? t("editPaymentMethod") : t("addPaymentMethod")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                <Label htmlFor="provider_name">{t("providerName")}</Label>
                        <Input
                  id="provider_name"
                  value={formData.provider_name}
                  onChange={(e) => setFormData({ ...formData, provider_name: e.target.value })}
                  placeholder={t("enterProviderName")}
                        />
                      </div>
                      <div className="space-y-2">
                <Label htmlFor="provider_code">{t("providerCode")}</Label>
                        <Input
                  id="provider_code"
                  value={formData.provider_code}
                  onChange={(e) => setFormData({ ...formData, provider_code: e.target.value })}
                  placeholder={t("enterProviderCode")}
                        />
                      </div>
                    </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t("description")}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t("enterDescription")}
                    />
                </div>
                      <div className="space-y-2">
              <Label htmlFor="image_url">{t("imageUrl")}</Label>
                        <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder={t("enterImageUrl")}
                        />
                      </div>
            <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                <Label htmlFor="status">{t("status")}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'active' | 'blocked') => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("active")}</SelectItem>
                    <SelectItem value="blocked">{t("blocked")}</SelectItem>
                  </SelectContent>
                </Select>
                      </div>
              <div className="space-y-2">
                <Label htmlFor="display_order">{t("displayOrder")}</Label>
                        <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                    </div>
            <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                <Label htmlFor="public_key">{t("publicKey")}</Label>
                        <Input
                  id="public_key"
                  value={formData.public_key}
                  onChange={(e) => setFormData({ ...formData, public_key: e.target.value })}
                  placeholder={t("enterPublicKey")}
                        />
                      </div>
                      <div className="space-y-2">
                <Label htmlFor="secret_key">{t("secretKey")}</Label>
                        <Input
                  id="secret_key"
                          type="password"
                  value={formData.secret_key}
                  onChange={(e) => setFormData({ ...formData, secret_key: e.target.value })}
                          placeholder={t("enterSecretKey")}
                        />
                      </div>
                    </div>
            <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                <Label htmlFor="merchant_id">{t("merchantId")}</Label>
                        <Input
                  id="merchant_id"
                  value={formData.merchant_id}
                  onChange={(e) => setFormData({ ...formData, merchant_id: e.target.value })}
                  placeholder={t("enterMerchantId")}
                        />
                      </div>
                      <div className="space-y-2">
                <Label htmlFor="callback_url">{t("callbackUrl")}</Label>
                        <Input
                  id="callback_url"
                  value={formData.callback_url}
                  onChange={(e) => setFormData({ ...formData, callback_url: e.target.value })}
                  placeholder={t("enterCallbackUrl")}
                        />
                      </div>
                    </div>
                </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreatePaymentMethodOpen(false);
              setIsEditPaymentMethodOpen(false);
              setEditingPaymentMethod(null);
              resetForm();
            }}>
              {t("cancel")}
                </Button>
           <Button 
             onClick={handleSubmit}
             disabled={createPaymentMethodMutation.isPending || updatePaymentMethodMutation.isPending}
           >
             {createPaymentMethodMutation.isPending || updatePaymentMethodMutation.isPending ? (
               <>
                 <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                 {t("saving")}
               </>
             ) : (
               <>
                 <Save className="mr-2 h-4 w-4" />
                 {editingPaymentMethod ? t("updatePaymentMethod") : t("createPaymentMethod")}
               </>
             )}
           </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Payment Method Dialog */}
      <Dialog open={isViewPaymentMethodOpen} onOpenChange={setIsViewPaymentMethodOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("paymentMethodDetails")}</DialogTitle>
            <DialogDescription>{t("viewPaymentMethodDetails")}</DialogDescription>
          </DialogHeader>
          {selectedPaymentMethod && (
              <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                      <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("providerName")}</Label>
                  <p className="text-sm">{selectedPaymentMethod.provider_name}</p>
                      </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("providerCode")}</Label>
                  <p className="text-sm">{selectedPaymentMethod.provider_code}</p>
                    </div>
                  </div>
                  
              <div>
                <Label className="text-sm font-medium text-muted-foreground">{t("description")}</Label>
                <p className="text-sm">{selectedPaymentMethod.description}</p>
                      </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("status")}</Label>
                  <Badge 
                    variant={selectedPaymentMethod.status === 'active' ? 'default' : 'secondary'}
                    className={selectedPaymentMethod.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                  >
                    {t(selectedPaymentMethod.status)}
                  </Badge>
                      </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("displayOrder")}</Label>
                  <p className="text-sm">{selectedPaymentMethod.display_order}</p>
                      </div>
                      </div>
              
              <div>
                <Label className="text-sm font-medium text-muted-foreground">{t("merchantId")}</Label>
                <p className="text-sm">{selectedPaymentMethod.merchant_id}</p>
                </div>

                      <div>
                <Label className="text-sm font-medium text-muted-foreground">{t("callbackUrl")}</Label>
                <p className="text-sm break-all">{selectedPaymentMethod.callback_url}</p>
                  </div>
                  
              {selectedPaymentMethod.image_url && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("providerLogo")}</Label>
                  <div className="mt-2">
                    <img 
                      src={selectedPaymentMethod.image_url} 
                      alt={selectedPaymentMethod.provider_name}
                      className="w-16 h-16 rounded object-cover border"
                        />
                      </div>
                      </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("createdAt")}</Label>
                  <p className="text-sm">{new Date(selectedPaymentMethod.createdAt).toLocaleString()}</p>
                      </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t("lastUpdated")}</Label>
                  <p className="text-sm">{new Date(selectedPaymentMethod.updatedAt).toLocaleString()}</p>
                      </div>
                      </div>
                    </div>
                  )}
        </DialogContent>
      </Dialog>

      {/* Create Setting Dialog */}
      <Dialog open={isCreateSettingOpen} onOpenChange={setIsCreateSettingOpen}>
        <DialogContent className="max-w-md">
                      <DialogHeader>
            <DialogTitle>{t("createSetting")}</DialogTitle>
            <DialogDescription>{t("createSettingDescription")}</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="setting-key">{t("key")} *</Label>
                          <Input
                id="setting-key"
                value={createSettingData.key}
                onChange={(e) => setCreateSettingData({ ...createSettingData, key: e.target.value })}
                placeholder={t("enterSettingKey")}
              />
              <p className="text-xs text-muted-foreground">{t("keyFormatDescription")}</p>
                        </div>
            <div className="space-y-2">
              <Label htmlFor="setting-value">{t("value")} *</Label>
                          <Input
                id="setting-value"
                type={createSettingData.is_encrypted ? "password" : "text"}
                value={createSettingData.value}
                onChange={(e) => setCreateSettingData({ ...createSettingData, value: e.target.value })}
                placeholder={t("enterSettingValue")}
                          />
                        </div>
            <div className="space-y-2">
              <Label htmlFor="setting-group">{t("group")} *</Label>
                          <Select 
                value={createSettingData.group}
                onValueChange={(value: 'sms' | 'firebase' | 'email' | 'general') => 
                  setCreateSettingData({ ...createSettingData, group: value })
                }
              >
                <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                  <SelectItem value="general">{t("general")}</SelectItem>
                  <SelectItem value="sms">{t("sms")}</SelectItem>
                  <SelectItem value="firebase">{t("firebase")}</SelectItem>
                  <SelectItem value="email">{t("email")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
            <div className="space-y-2">
              <Label htmlFor="setting-description">{t("description")}</Label>
              <Textarea
                id="setting-description"
                value={createSettingData.description}
                onChange={(e) => setCreateSettingData({ ...createSettingData, description: e.target.value })}
                placeholder={t("enterSettingDescription")}
                rows={2}
              />
            </div>
            <div className="flex items-center space-x-2">
                          <Switch
                id="setting-encrypted"
                checked={createSettingData.is_encrypted}
                onCheckedChange={(checked) => 
                  setCreateSettingData({ ...createSettingData, is_encrypted: checked })
                }
              />
              <Label htmlFor="setting-encrypted">{t("encryptValue")}</Label>
                        </div>
                      </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateSettingOpen(false)}>
                          {t("cancel")}
                        </Button>
                        <Button
              onClick={handleSubmitCreateSetting}
              disabled={createSettingMutation.isPending}
            >
              {createSettingMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("creating")}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createSetting")}
                </>
              )}
                        </Button>
          </DialogFooter>
                    </DialogContent>
                  </Dialog>

      {/* Create/Edit Role Dialog */}
      <Dialog open={isCreateRoleOpen || isEditRoleOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateRoleOpen(false);
          setIsEditRoleOpen(false);
          setEditingRole(null);
          resetRoleForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingRole ? t("editRole") : t("createRole")}
            </DialogTitle>
            <DialogDescription>
              {editingRole ? t("editRoleDescription") : t("createRoleDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">{t("name")} *</Label>
              <Input
                id="role-name"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t("enterRoleName")}
              />
                </div>
            <div className="space-y-2">
              <Label htmlFor="role-description">{t("description")} *</Label>
              <Textarea
                id="role-description"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t("enterRoleDescription")}
                rows={3}
              />
                        </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="role-active"
                checked={roleFormData.isActive}
                onCheckedChange={(checked: boolean) => setRoleFormData(prev => ({ 
                  ...prev, 
                  isActive: checked 
                }))}
              />
              <Label htmlFor="role-active">
                {t("activeRole")}
              </Label>
                        </div>
                      </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateRoleOpen(false);
              setIsEditRoleOpen(false);
              setEditingRole(null);
              resetRoleForm();
            }}>
              {t("cancel")}
                        </Button>
                          <Button
              onClick={handleSubmitRole}
              disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
            >
              {(createRoleMutation.isPending || updateRoleMutation.isPending) ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {editingRole ? t("updating") : t("creating")}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {editingRole ? t("updateRole") : t("createRole")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Setting Dialog */}
      <Dialog open={isEditSettingOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditSettingOpen(false);
          setEditingSetting(null);
          resetCreateSettingForm();
        }
      }}>
        <DialogContent className="max-w-md">
                  <DialogHeader>
            <DialogTitle>{t("editSetting")}</DialogTitle>
            <DialogDescription>{t("editSettingDescription")}</DialogDescription>
                  </DialogHeader>
                    <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-setting-key">{t("key")} *</Label>
                        <Input
                id="edit-setting-key"
                value={createSettingData.key}
                disabled={true} // Key cannot be changed when editing
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">{t("keyCannotBeChanged")}</p>
                      </div>
            <div className="space-y-2">
              <Label htmlFor="edit-setting-value">{t("value")} *</Label>
                        <Input
                id="edit-setting-value"
                value={createSettingData.value}
                onChange={(e) => setCreateSettingData(prev => ({ ...prev, value: e.target.value }))}
                placeholder={t("enterSettingValue")}
                        />
                      </div>
            <div className="space-y-2">
              <Label htmlFor="edit-setting-group">{t("group")}</Label>
                        <Select 
                value={createSettingData.group}
                onValueChange={(value) => setCreateSettingData(prev => ({ 
                  ...prev, 
                  group: value as 'sms' | 'firebase' | 'email' | 'general' 
                }))}
                disabled={true} // Group cannot be changed when editing
              >
                <SelectTrigger className="bg-muted">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                  <SelectItem value="general">{t("general")}</SelectItem>
                  <SelectItem value="sms">{t("sms")}</SelectItem>
                  <SelectItem value="firebase">{t("firebase")}</SelectItem>
                  <SelectItem value="email">{t("email")}</SelectItem>
                          </SelectContent>
                        </Select>
              <p className="text-xs text-muted-foreground">{t("groupCannotBeChanged")}</p>
                      </div>
            <div className="space-y-2">
              <Label htmlFor="edit-setting-description">{t("description")}</Label>
              <Input
                id="edit-setting-description"
                value={createSettingData.description}
                onChange={(e) => setCreateSettingData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t("enterSettingDescription")}
                        />
                      </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-setting-encrypted"
                checked={createSettingData.is_encrypted}
                onCheckedChange={(checked: boolean) => setCreateSettingData(prev => ({ 
                  ...prev, 
                  is_encrypted: checked 
                }))}
                disabled={true} // Encryption cannot be changed when editing
              />
              <Label htmlFor="edit-setting-encrypted" className="text-muted-foreground">
                {t("encryptValue")} ({t("cannotBeChanged")})
              </Label>
                    </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditSettingOpen(false);
              setEditingSetting(null);
              resetCreateSettingForm();
            }}>
                      {t("cancel")}
                    </Button>
            <Button
              onClick={handleSubmitCreateSetting}
              disabled={updateSettingMutation.isPending}
            >
              {updateSettingMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("updating")}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t("updateSetting")}
                </>
              )}
                    </Button>
          </DialogFooter>
                </DialogContent>
              </Dialog>

      {/* Create/Edit Permission Dialog */}
      <Dialog open={isCreatePermissionOpen || isEditPermissionOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreatePermissionOpen(false);
          setIsEditPermissionOpen(false);
          setEditingPermission(null);
          resetPermissionForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPermission ? t("editPermission") : t("createPermission")}
            </DialogTitle>
            <DialogDescription>
              {editingPermission ? t("editPermissionDescription") : t("createPermissionDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
                <div className="space-y-2">
              <Label htmlFor="permission-name">{t("name")}</Label>
              <Input
                id="permission-name"
                placeholder={t("enterPermissionName")}
                value={permissionFormData.name}
                onChange={(e) => setPermissionFormData(prev => ({ ...prev, name: e.target.value }))}
              />
                </div>
                <div className="space-y-2">
              <Label htmlFor="permission-description">{t("description")}</Label>
              <Textarea
                id="permission-description"
                placeholder={t("enterPermissionDescription")}
                value={permissionFormData.description}
                onChange={(e) => setPermissionFormData(prev => ({ ...prev, description: e.target.value }))}
              />
                </div>
                <div className="space-y-2">
              <Label htmlFor="permission-resource">{t("resource")}</Label>
              <Input
                id="permission-resource"
                placeholder={t("enterResource")}
                value={permissionFormData.resource}
                onChange={(e) => setPermissionFormData(prev => ({ ...prev, resource: e.target.value }))}
              />
                </div>
                <div className="space-y-2">
              <Label htmlFor="permission-action">{t("action")}</Label>
              <Input
                id="permission-action"
                placeholder={t("enterAction")}
                value={permissionFormData.action}
                onChange={(e) => setPermissionFormData(prev => ({ ...prev, action: e.target.value }))}
              />
                </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="permission-active"
                checked={permissionFormData.isActive}
                onCheckedChange={(checked: boolean) => setPermissionFormData(prev => ({ 
                  ...prev, 
                  isActive: checked 
                }))}
              />
              <Label htmlFor="permission-active">{t("activePermission")}</Label>
              </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreatePermissionOpen(false);
              setIsEditPermissionOpen(false);
              setEditingPermission(null);
              resetPermissionForm();
            }}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmitPermission}
              disabled={createPermissionMutation.isPending || updatePermissionMutation.isPending}
            >
              {(createPermissionMutation.isPending || updatePermissionMutation.isPending) ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {editingPermission ? t("updating") : t("creating")}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {editingPermission ? t("updatePermission") : t("createPermission")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
